import { createSelector } from 'reselect';
import {
    handleRehydrateAction,
    handleRemoveAction,
    handleSubmitAction
} from '../../../core/store/helpers/reducer-helper';
import { removeRefListItem } from '../../../core/store/helpers/remove-ref-list-item';
import { intersect, omit } from '../../../core/util/utils';
import * as fromAccessGroupActions from '../access-group/access-group.actions';
import * as fromContactTypeActions from '../contact-type/contact-type.actions';
import * as fromJobStatusActions from '../job-status/job-status.actions';
import * as fromJobTypeActions from '../job-type/job-type.actions';
import * as fromLookupTypeActions from '../lookup-type/lookup-type.actions';
import * as fromUserActions from '../user/user.actions';
import { User } from '../user/user.model';
import * as fromMembershipActions from './membership.actions';
import { Membership } from './membership.model';

export interface State {
    ids: Array<string>;
    entities: { [id: string]: Membership };
}

export const initialState: State = {
    ids: [],
    entities: {}
};

// tslint:disable-next-line:only-arrow-functions
export function reducer(
    state = initialState,
    action:
        fromMembershipActions.Actions |
        fromUserActions.Actions |
        fromAccessGroupActions.Actions |
        fromContactTypeActions.Actions |
        fromLookupTypeActions.Actions |
        fromJobTypeActions.Actions |
        fromJobStatusActions.Actions
): State {
    switch (action.type) {
        case fromMembershipActions.SUBMIT: {
            return handleSubmitAction(state, action);
        }
        case fromMembershipActions.REHYDRATE: {
            return handleRehydrateAction(state, action);
        }
        case fromMembershipActions.REMOVE: {
            return handleRemoveAction(state, action);
        }
        case fromAccessGroupActions.REMOVE: {
            const entities = {};

            state.ids.forEach(id => {
                entities[id] = removeRefListItem(state.entities[id], 'accessGroups', action.payload.id);
            });

            return {
                ids: state.ids,
                entities
            };
        }
        case fromContactTypeActions.REMOVE: {
            const entities = {};

            state.ids.forEach(id => {
                entities[id] = removeRefListItem(state.entities[id], 'contactTypes', action.payload.id);
            });

            return {
                ids: state.ids,
                entities
            };
        }
        case fromLookupTypeActions.REMOVE: {
            const entities = {};

            state.ids.forEach(id => {
                entities[id] = removeRefListItem(state.entities[id], 'lookupTypes', action.payload.id);
            });

            return {
                ids: state.ids,
                entities
            };
        }
        case fromJobTypeActions.REMOVE: {
            const entities = {};

            state.ids.forEach(id => {
                entities[id] = removeRefListItem(state.entities[id], 'jobTypes', action.payload.id);
            });

            return {
                ids: state.ids,
                entities
            };
        }
        case fromJobStatusActions.REMOVE: {
            const entities = {};

            state.ids.forEach(id => {
                entities[id] = removeRefListItem(state.entities[id], 'jobStatuses', action.payload.id);
            });

            return {
                ids: state.ids,
                entities
            };
        }
        case fromUserActions.REMOVE: {
            const item = action.payload as User;

            if (!item) {
                throw new Error('payload cannot be null');
            }

            if (!item.id || !item.id.length) {
                throw new Error('id cannot be null');
            }

            // if the user's memberships do not exist in our state
            const membershipsToRemove = intersect(state.ids, item.memberships);
            if (membershipsToRemove.length === 0) {
                return state;
            }

            return {
                ids: state.ids.filter(id => membershipsToRemove.indexOf(id) === -1),
                entities: omit(state.entities, membershipsToRemove)
            };

        }

        default: {
            return state;
        }
    }
}

/**
 * Because the data structure is defined within the reducer it is optimal to
 * locate our selector functions at this level. If store is to be thought of
 * as a database, and reducers the tables, selectors can be considered the
 * queries into said database. Remember to keep your selectors small and
 * focused so they can be combined and composed to fit each particular
 * use-case.
 */

export const getEntities = (state: State) => state.entities;

export const getIds = (state: State) => state.ids;

export const getAll = createSelector(getEntities, getIds, (entities, ids) => ids.map(id => entities[id]));



// WEBPACK FOOTER //
// ./src/vas/business-objects/membership/membership.reducer.ts